import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Card,
  Avatar,
  Typography,
  Box,
  InputAdornment,
  Skeleton,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Alert,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon
} from '@mui/material';
import {
  Search,
  Assignment,
  Visibility,
  Close,
  Email,
  Phone,
  LocationOn,
  AttachFile,
  GetApp,
  VideoCall,
  Person,
  Business,
  CalendarToday,
  CheckCircle,
  Task,
  Subject,
  Description,
  Support as SupportIcon,
  ArrowDropDown,
  PersonOutline,
  Chat as ChatIcon,
  Send
} from '@mui/icons-material';
import axios from 'axios';
import { useSocket } from '../../components/Hooks/SocketProvider';
import { BASE_URL } from '../../services/configUrls';

const InstituteTicket = () => {
  const [meetings, setMeetings] = useState([]);
  const [totalPending, setTotalPending] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  
  const [openSolveDialog, setOpenSolveDialog] = useState(false);
  const [closingRemark, setClosingRemark] = useState('');
  const [solvingLoading, setSolvingLoading] = useState(false);
  const [solveError, setSolveError] = useState(null);
  const [solveSuccess, setSolveSuccess] = useState(null);

  const [anchorEl, setAnchorEl] = useState(null);
  const [supportTeam, setSupportTeam] = useState(null);
  const [loadingSupportTeam, setLoadingSupportTeam] = useState(false);
  const [supportError, setSupportError] = useState(null);
  const [currentMeetingForSupport, setCurrentMeetingForSupport] = useState(null);

  const [openChatDialog, setOpenChatDialog] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [loadingChat, setLoadingChat] = useState(false);
  const [chatError, setChatError] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  const { socket, lastMessage, userId, connectionStatus } = useSocket();

  // ✅ CRITICAL: Refs for WebSocket callbacks
  const chatMessagesRef = useRef(chatMessages);
  const chatModalRequestRef = useRef(null);
  const isChatModalOpenRef = useRef(openChatDialog);
  const chatMessagesEndRef = useRef(null);

  // Sync refs with state
  useEffect(() => {
    chatMessagesRef.current = chatMessages;
    scrollToBottom();
  }, [chatMessages]);

  useEffect(() => {
    console.log("📌 Selected meeting updated:", selectedMeeting?.meeting_id);
    chatModalRequestRef.current = selectedMeeting;
  }, [selectedMeeting]);

  useEffect(() => {
    console.log("📌 Chat dialog open:", openChatDialog);
    isChatModalOpenRef.current = openChatDialog;
  }, [openChatDialog]);

  const getAccessToken = () => {
    return localStorage.getItem('accessToken') || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJlNDBmNWQ5NS04MzAwLTQ1MWEtYTZlZi0wYWZiN2FkNGFmYmMiLCJleHAiOjE3NjI0OTU5NDB9.y0SHwlfk8mDlRVVpuQwfpO89_RFl3I1JA7JAQt6JGjE';
  };

  const scrollToBottom = () => {
    chatMessagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // ✅ IMPROVED: WebSocket listener with robust parser (like staff side)
  // ✅ FIXED: WebSocket listener that handles BOTH string and object formats
// ✅ FIXED: WebSocket listener for Support side
useEffect(() => {
  console.log("🔌 Setting up WebSocket effect");
  if (!socket) {
    console.warn("⚠️ No WebSocket available");
    return;
  }

  console.log("🔌 Setting up WebSocket listener");
  console.log("Socket state:", socket.readyState);

  const handleWebSocketMessage = (event) => {
    console.log("📩 ========== NEW WEBSOCKET MESSAGE ==========");
    console.log("📩 Raw event.data:", event.data);
    console.log("📩 Type of event.data:", typeof event.data);

    try {
      let messageData;

      // ✅ Parse message (handle both string and object formats)
      if (typeof event.data === 'object') {
        messageData = event.data;
      } else if (typeof event.data === 'string') {
        let parsed = event.data;
        let attempts = 0;
        const maxAttempts = 3;

        while (typeof parsed === "string" && attempts < maxAttempts) {
          try {
            const nextParse = JSON.parse(parsed);
            parsed = nextParse;
            attempts++;
            console.log(`🔍 Parse attempt ${attempts}:`, parsed);
          } catch (e) {
            console.log("⚠️ Parse stopped at attempt", attempts);
            break;
          }
        }
        
        messageData = parsed;
      } else {
        console.warn("⚠️ Unknown data type:", typeof event.data);
        return;
      }

      console.log("✅ Final messageData:", messageData);

      // Get refs (not state!)
      const isChatOpen = isChatModalOpenRef.current;
      const currentMeeting = chatModalRequestRef.current;
      const currentMeetingId = currentMeeting?.meeting_id;

      console.log("📊 Current state:", {
        isChatOpen,
        currentMeetingId,
        hasStatus: messageData.status,
        hasChatResponse: !!messageData.chat_response,
      });

      // ✅ CRITICAL: Check for chat_response in the message
      if (messageData.chat_response) {
        const chatResponse = messageData.chat_response;
        const messageMeetingId = chatResponse.staff_meting_id || chatResponse.staff_meeting_id;

        console.log("🔍 Message details:", {
          messageMeetingId,
          currentMeetingId,
          message: chatResponse.message,
          isSpoc: chatResponse.is_spoc,
          messageFrom: chatResponse.made_by_user_id,
          currentUser: userId,
        });

        // ✅ FIXED: Accept messages for current meeting from ALL users (both SPOC and Support)
        // Convert to strings for reliable comparison
        if (
          isChatOpen && 
          currentMeetingId && 
          String(messageMeetingId) === String(currentMeetingId)
        ) {
          console.log("✅ ✅ ✅ ACCEPTING MESSAGE FOR CURRENT MEETING");
          console.log("Message:", chatResponse.message);
          console.log("From:", chatResponse.is_spoc === "1" ? "Staff/SPOC" : "Support");

          setChatMessages((prevMessages) => {
            console.log("📋 Previous messages count:", prevMessages.length);

            // ✅ FIXED: Only remove "sending" messages if it's from the current user (Support)
           const isOwnMessage = String(chatResponse.made_by_user_id) === String(userId);

            console.log("🔍 Is own message?", isOwnMessage, {
              messageUserId: chatResponse.made_by_user_id,
              currentUserId: userId,
              isSpoc: chatResponse.is_spoc,
            });

            let filteredMessages = prevMessages;
            if (isOwnMessage) {
              // Remove "sending" messages only for own messages
              filteredMessages = prevMessages.filter((msg) => !msg.sending);
              console.log("🗑️ Filtered out 'sending' messages (own message)");
            }

            // Check if message already exists (avoid duplicates)
            const messageExists = filteredMessages.some(
              (msg) =>
                msg.id === chatResponse.id ||
                (msg.message === chatResponse.message &&
                  Math.abs(new Date(msg.created_at) - new Date(chatResponse.created_at)) < 1000)
            );

            if (messageExists) {
              console.log("⚠️ Message already exists, skipping duplicate");
              return filteredMessages;
            }

            console.log("➕ Adding new message to UI");
            console.log("Message data:", {
              id: chatResponse.id,
              message: chatResponse.message,
              is_spoc: chatResponse.is_spoc,
              from: chatResponse.is_spoc === "1" ? "Staff/SPOC" : "Support (You)",
            });

            // Add the new messageli            return [...filteredMessages, chatResponse];
          });

          console.log("✅ Message successfully added to UI!");
        } else {
          console.log("❌ ❌ ❌ MESSAGE REJECTED");
          console.log("Rejection details:", {
            chatOpen: isChatOpen,
            hasMeetingId: !!currentMeetingId,
            messageMeetingId: String(messageMeetingId),
            currentMeetingId: String(currentMeetingId),
            idsMatch: String(messageMeetingId) === String(currentMeetingId),
          });
          
          if (!isChatOpen) {
            console.log("  ❌ Reason: Chat dialog is NOT open");
          } else if (!currentMeetingId) {
            console.log("  ❌ Reason: No current meeting ID set in ref");
          } else if (String(messageMeetingId) !== String(currentMeetingId)) {
            console.log("  ❌ Reason: Meeting IDs don't match");
            console.log("     Expected:", String(currentMeetingId));
            console.log("     Received:", String(messageMeetingId));
          }
        }
      } else {
        console.log("⚠️ No chat_response found in message");
        console.log("Available fields:", Object.keys(messageData));
      }
    } catch (error) {
      console.error("💥 ERROR in WebSocket handler:", error);
      console.error("Error message:", error.message);
      console.error("Error stack:", error.stack);
    }
    
    console.log("📩 ========== END MESSAGE PROCESSING ==========\n");
  };

  console.log("🔌 Adding WebSocket listener");
  socket.addEventListener("message", handleWebSocketMessage);

  return () => {
    console.log("🔌 Removing WebSocket listener");
    socket.removeEventListener("message", handleWebSocketMessage);
  };
}, [socket, userId]);



  // Fetch chat messages for a meeting
  const fetchChatMessages = async (meetingId) => {
    console.log("📥 Fetching chat messages for meeting:", meetingId);
    setLoadingChat(true);
    setChatError(null);
    
    try {
      const config = {
        method: 'get',
        maxBodyLength: Infinity,
        url: `${BASE_URL}/staffmetting/staffMeetingChat/${meetingId}`,
        headers: { 
          'Authorization': `Bearer ${getAccessToken()}`
        }
      };

      const response = await axios.request(config);
      console.log('✅ Chat messages response:', response.data);
      
      if (response.data && response.data.messages) {
        setChatMessages(response.data.messages);
      } else {
        setChatMessages([]);
      }
    } catch (error) {
      console.error('❌ Error fetching chat messages:', error);
      if (error.response) {
        setChatError(`Error ${error.response.status}: ${error.response.data?.message || 'Unable to load chat messages'}`);
      } else if (error.request) {
        setChatError('Network error. Please check your connection.');
      } else {
        setChatError('Unable to load chat messages. Please try again.');
      }
    } finally {
      setLoadingChat(false);
    }
  };

  // ✅ IMPROVED: Send message function (like staff side)
  const sendChatMessage = async () => {
    if (!newMessage.trim() || !selectedMeeting) {
      return;
    }

    if (!socket || socket.readyState !== WebSocket.OPEN) {
      setChatError('Chat connection not available. Please refresh the page.');
      return;
    }

    setSendingMessage(true);
    
    try {
      const messageData = {
        staff_meting_id: selectedMeeting.meeting_id,
        message: newMessage.trim(),
        made_by_user_id: userId,
        role: "staff",
        is_spoc: "0"  // ✅ Support = "0", Staff = "1"
      };

      console.log('📤 Sending message via WebSocket:', messageData);
      
      // ✅ Optimistic update - add message immediately with "sending" flag
      const tempMessage = {
        id: `temp-${Date.now()}`,
        message: newMessage.trim(),
        role: "staff",
        is_spoc: "0",
        created_at: new Date().toISOString(),
        made_by_user_id: userId,
        staff_meting_id: selectedMeeting.meeting_id,
        sending: true  // Shows loading indicator
      };
      
      setChatMessages(prev => [...prev, tempMessage]);
      
      // Send via WebSocket
      socket.send(JSON.stringify(messageData));
      console.log('✅ Message sent via WebSocket');
      
      // Clear input field
      setNewMessage('');
    } catch (error) {
      console.error('❌ Error sending message:', error);
      setChatError('Failed to send message. Please try again.');
      
      // Remove failed message from UI
      setChatMessages(prev => prev.filter(msg => !msg.sending));
    } finally {
      setSendingMessage(false);
    }
  };

  // ✅ IMPROVED: Open chat dialog handler
  const handleOpenChatDialog = (meeting) => {
    console.log("========== OPENING CHAT ==========");
    console.log("Meeting ID:", meeting?.meeting_id);
    console.log("Connection Status:", connectionStatus);

    // ✅ CRITICAL: Set refs FIRST, before any state updates
    chatModalRequestRef.current = meeting;
    isChatModalOpenRef.current = true;

    console.log("✅ Refs set IMMEDIATELY:");
    console.log("- Meeting ID in ref:", chatModalRequestRef.current?.meeting_id);
    console.log("- Chat open in ref:", isChatModalOpenRef.current);

    // Now set state
    setSelectedMeeting(meeting);
    setOpenChatDialog(true);

    // Clear and reset
    setChatMessages([]);
    setNewMessage("");
    setChatError(null);

    // Fetch history
    console.log("📥 Fetching chat messages...");
    fetchChatMessages(meeting.meeting_id);
    console.log("========== CHAT OPEN COMPLETE ==========");
  };

  const handleCloseChatDialog = () => {
    console.log("🔒 Closing chat dialog");

    setOpenChatDialog(false);
    setSelectedMeeting(null);

    // Clear refs
    isChatModalOpenRef.current = false;
    chatModalRequestRef.current = null;

    setChatMessages([]);
    setNewMessage("");
    setChatError(null);
  };

  const formatChatTimestamp = (dateString) => {
    try {
      return new Date(dateString).toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return dateString;
    }
  };

  const getMessageSenderInfo = (message) => {
    const isSpoc = message.is_spoc === "1" || message.is_spoc === 1;
    return {
      isSpoc,
      label: isSpoc ? 'SPOC' : 'Support',
      color: isSpoc ? '#3b82f6' : '#10b981',
      bgColor: isSpoc ? '#dbeafe' : '#d1fae5',
      avatarBg: isSpoc ? 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
    };
  };

  const fetchSupportTeam = async (meeting) => {
    setLoadingSupportTeam(true);
    setSupportError(null);
    
    try {
      const formData = new FormData();
      formData.append('subjects', meeting.topic || 'Staff meeting support request');
      formData.append('description', meeting.description || 'Support needed for staff meeting');

      const config = {
        method: 'get',
        maxBodyLength: Infinity,
        url: `${BASE_URL}/staffmetting/validate_dropdown_emails`,
        headers: { 
          'Authorization': `Bearer ${getAccessToken()}`
        },
        data: formData
      };

      const response = await axios.request(config);
      console.log('Support Team Response:', response.data);
      
      if (response.data) {
        setSupportTeam(response.data);
      } else {
        setSupportError('No support team data found');
      }
    } catch (error) {
      console.error('Error fetching support team:', error);
      if (error.response) {
        setSupportError(`Error ${error.response.status}: ${error.response.data?.message || 'Unable to load support team'}`);
      } else if (error.request) {
        setSupportError('Network error. Please check your connection.');
      } else {
        setSupportError('Unable to load support team. Please try again.');
      }
    } finally {
      setLoadingSupportTeam(false);
    }
  };

  const handleSupportDropdownClick = (event, meeting) => {
    setAnchorEl(event.currentTarget);
    setCurrentMeetingForSupport(meeting);
    setSupportTeam(null);
    fetchSupportTeam(meeting);
  };

  const handleSupportDropdownClose = () => {
    setAnchorEl(null);
  };

  const handleSupportMemberSelect = async (memberKey, memberData) => {
    console.log('Selected support member:', memberData);
    console.log('For meeting:', currentMeetingForSupport);
    
    try {
      const data = JSON.stringify({
        staff_meetings_id: currentMeetingForSupport.meeting_id,
        assigned_userid: memberData.user_id
      });

      const config = {
        method: 'post',
        maxBodyLength: Infinity,
        url: `${BASE_URL}/staffmetting/updateStaffQuestion`,
        headers: { 
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${getAccessToken()}`
        },
        data: data
      };

      setSupportError(null);
      setLoadingSupportTeam(true);

      const response = await axios.request(config);
      
      console.log('Assignment response:', response.data);

      setMeetings(prevMeetings => 
        prevMeetings.map(meeting => 
          meeting.meeting_id === currentMeetingForSupport.meeting_id 
            ? { ...meeting, assigned_userid: memberData.user_id, assigned_user_name: memberData.role_name }
            : meeting
        )
      );

      alert(`Successfully assigned to: ${memberData.role_name}\nEmail: ${memberData.email}\nUser ID: ${memberData.user_id}\n\nMeeting: ${currentMeetingForSupport.topic}`);
      
      handleSupportDropdownClose();
      
    } catch (error) {
      console.error('Error assigning support member:', error);
      
      let errorMessage = 'Failed to assign support member. ';
      if (error.response) {
        errorMessage += `Error ${error.response.status}: ${error.response.data?.message || 'Please try again.'}`;
      } else if (error.request) {
        errorMessage += 'Network error. Please check your connection.';
      } else {
        errorMessage += 'An error occurred. Please try again.';
      }
      
      setSupportError(errorMessage);
      alert(errorMessage);
    } finally {
      setLoadingSupportTeam(false);
    }
  };

  const fetchMeetings = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const config = {
        method: 'get',
        maxBodyLength: Infinity,
        url: `${BASE_URL}/staffmetting/pendingTickets_2`,
        headers: { 
          'Authorization': `Bearer ${getAccessToken()}`
        }
      };

      const response = await axios.request(config);
      console.log('API Response:', response.data);
      
      if (response.data && response.data.data) {
        setMeetings(response.data.data);
        setTotalPending(response.data.total_pending || 0);
      } else {
        setError('No meetings data found');
      }
    } catch (error) {
      console.error('Error fetching meetings:', error);
      if (error.response) {
        const status = error.response.status;
        if (status === 401) {
          setError('Authentication failed. Please check your access token.');
        } else if (status === 403) {
          setError('Access denied. You do not have permission to view this data.');
        } else if (status === 404) {
          setError('API endpoint not found. Please check the server configuration.');
        } else if (status >= 500) {
          setError('Server error. Please try again later.');
        } else {
          setError(`Error ${status}: ${error.response.data?.message || 'Unknown error occurred'}`);
        }
      } else if (error.request) {
        setError('Network error. Please check your internet connection and server status.');
      } else {
        setError('Unable to load meetings. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMeetings();
  }, []);

  const filteredMeetings = meetings.filter(meeting => 
    meeting.educator_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    meeting.topic?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    meeting.institute_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    meeting.educator_email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleOpenViewDialog = (meeting) => {
    setSelectedMeeting(meeting);
    setOpenViewDialog(true);
  };

  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
    setSelectedMeeting(null);
  };

  const handleOpenSolveDialog = (meeting) => {
    setSelectedMeeting(meeting);
    setClosingRemark('');
    setSolveError(null);
    setSolveSuccess(null);
    setOpenSolveDialog(true);
  };

  const handleCloseSolveDialog = () => {
    setOpenSolveDialog(false);
    setSelectedMeeting(null);
    setClosingRemark('');
    setSolveError(null);
    setSolveSuccess(null);
  };

  const handleSolveMeeting = async () => {
    if (!closingRemark.trim()) {
      setSolveError('Please provide a closing remark explaining how the issue has been resolved.');
      return;
    }

    setSolvingLoading(true);
    setSolveError(null);

    try {
      const data = JSON.stringify({
        staff_meetings_id: selectedMeeting.meeting_id,
        status: "solved",
        closing_remark: closingRemark.trim()
      });

      console.log('Sending solve request with data:', data);

      const config = {
        method: 'post',
        maxBodyLength: Infinity,
        url: `${BASE_URL}/staffmetting/updateSupportQuestion_3`,
        headers: { 
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${getAccessToken()}`
        },
        data: data
      };

      const response = await axios.request(config);
      console.log('Solve response:', response.data);
      
      if (response.data.status || response.status === 200) {
        setSolveSuccess('Meeting has been successfully marked as solved!');
        
        setMeetings(prevMeetings => 
          prevMeetings.filter(meeting => meeting.meeting_id !== selectedMeeting.meeting_id)
        );

        setTotalPending(prev => Math.max(0, prev - 1));

        setTimeout(() => {
          handleCloseSolveDialog();
        }, 2000);
      } else {
        setSolveError('Failed to update meeting status. Please try again.');
      }
    } catch (error) {
      console.error('Error solving meeting:', error);
      if (error.response) {
        const status = error.response.status;
        if (status === 401) {
          setSolveError('Authentication failed. Please login again.');
        } else if (status === 403) {
          setSolveError('You do not have permission to update this meeting.');
        } else {
          setSolveError(`Error ${status}: ${error.response.data?.message || 'Failed to update meeting status'}`);
        }
      } else if (error.request) {
        setSolveError('Network error. Please check your connection and try again.');
      } else {
        setSolveError('Error occurred while updating meeting status. Please try again.');
      }
    } finally {
      setSolvingLoading(false);
    }
  };

  const handleJoinMeeting = (meetingLink) => {
    if (meetingLink) {
      const url = meetingLink.startsWith('http') ? meetingLink : `https://${meetingLink}`;
      window.open(url, '_blank');
    }
  };

  const handleDownloadAttachment = (attachment) => {
    if (attachment.attachment_name) {
      window.open(attachment.attachment_name, '_blank');
    }
  };

  const getFileIcon = (url) => {
    const extension = url?.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf': return '📄';
      case 'doc':
      case 'docx': return '📝';
      case 'xls':
      case 'xlsx': return '📊';
      case 'png':
      case 'jpg':
      case 'jpeg': return '🖼️';
      case 'pages': return '📃';
      default: return '📎';
    }
  };

  const getFileName = (url) => {
    if (!url) return 'Unknown File';
    const fileName = url.split('/').pop();
    const cleanFileName = fileName.replace(/^[a-f0-9\-]{36}/i, '');
    return cleanFileName || fileName;
  };

  const getFileSize = (url) => {
    return 'Unknown size';
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(word => word[0]).join('').toUpperCase() || 'NA';
  };

  const getStatusChip = (meeting) => {
    const status = meeting.status || 'pending';
    const colors = {
      pending: { color: '#f59e0b', bg: '#fef3c7', text: 'Pending' },
      solved: { color: '#10b981', bg: '#d1fae5', text: 'Solved' },
      in_progress: { color: '#3b82f6', bg: '#dbeafe', text: 'In Progress' }
    };
    
    return colors[status] || colors.pending;
  };

  const getRoleBadgeColor = (roleKey) => {
    const colors = {
      support: { bg: '#dbeafe', color: '#1e40af', label: 'Support' },
      lead_ops: { bg: '#fef3c7', color: '#92400e', label: 'Lead Operations' },
      bd: { bg: '#d1fae5', color: '#065f46', label: 'Business Development' }
    };
    return colors[roleKey] || { bg: '#f3f4f6', color: '#374151', label: roleKey };
  };

  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: '#f8fafc' }}>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 flex items-center justify-center shadow-lg">
            <VideoCall className="text-white text-lg" />
          </div>
          <div>
            <Typography variant="h5" className="font-bold text-gray-900 tracking-tight">
              Pending Support Tickets
            </Typography>
            <Typography variant="body2" className="text-gray-500 font-medium text-xs">
              Total Pending: {totalPending} tickets
            </Typography>
          </div>
        </div>

        {/* WebSocket Connection Status */}
        <Card className="border-0 shadow-lg shadow-black/5 mb-4">
          <Box className="p-3 flex items-center gap-2">
            <div 
              className={`w-2 h-2 rounded-full ${
                connectionStatus === 'connected' ? 'bg-green-500' : 
                connectionStatus === 'connecting' ? 'bg-yellow-500' : 
                'bg-red-500'
              }`}
            />
            <Typography variant="body2" style={{ fontSize: '11px' }}>
              WebSocket: {connectionStatus} {userId && `(User ID: ${userId})`}
            </Typography>
          </Box>
        </Card>

        {/* Search */}
        <Card className="border-0 shadow-lg shadow-black/5 mb-6">
          <Box className="p-4">
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by educator name, topic, institute..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search className="text-gray-400" />
                  </InputAdornment>
                ),
                className: "rounded-lg bg-gray-50",
                style: { fontSize: '12px' }
              }}
            />
          </Box>
        </Card>
      </div>

      {/* Error Message */}
      {error && (
        <Card className="border-0 shadow-lg mb-6 bg-red-50 border-red-200">
          <Box className="p-4">
            <Alert severity="error" className="mb-2">
              <Typography variant="body2" className="text-red-700">
                {error}
              </Typography>
            </Alert>
            <Button
              size="small"
              onClick={fetchMeetings}
              className="mt-2"
              variant="outlined"
              color="error"
            >
              Retry
            </Button>
          </Box>
        </Card>
      )}

      {/* Table */}
      <Card className="border-0 shadow-lg shadow-black/5">
        <TableContainer component={Paper} style={{ borderRadius: '12px', overflow: 'hidden' }}>
          <Table>
            <TableHead>
              <TableRow style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                <TableCell style={{ color: 'white', fontWeight: 'bold', fontSize: '11px' }}>Meeting ID</TableCell>
                <TableCell style={{ color: 'white', fontWeight: 'bold', fontSize: '11px' }}>Educator</TableCell>
                <TableCell style={{ color: 'white', fontWeight: 'bold', fontSize: '11px' }}>Institute</TableCell>
                <TableCell style={{ color: 'white', fontWeight: 'bold', fontSize: '11px' }}>Topic</TableCell>
                <TableCell style={{ color: 'white', fontWeight: 'bold', fontSize: '11px' }}>Meeting Link</TableCell>
                <TableCell style={{ color: 'white', fontWeight: 'bold', fontSize: '11px' }}>Date</TableCell>
                <TableCell style={{ color: 'white', fontWeight: 'bold', fontSize: '11px' }}>Assigned To</TableCell>
                <TableCell style={{ color: 'white', fontWeight: 'bold', fontSize: '11px', textAlign: 'center' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from(new Array(5)).map((_, index) => (
                  <TableRow key={index}>
                    {Array.from(new Array(8)).map((_, cellIndex) => (
                      <TableCell key={cellIndex}>
                        <Skeleton animation="wave" height={30} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : filteredMeetings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Box className="py-12">
                      <Assignment style={{ fontSize: '64px', color: '#d1d5db' }} />
                      <Typography variant="body1" className="mt-4 text-gray-500" style={{ fontSize: '14px' }}>
                        {searchTerm ? 'No meetings found matching your search.' : 'No pending support tickets.'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                filteredMeetings.map((meeting) => {
                  const statusChip = getStatusChip(meeting);
                  return (
                    <TableRow 
                      key={meeting.meeting_id}
                      hover
                      style={{ cursor: 'pointer' }}
                    >
                      <TableCell style={{ fontSize: '11px', fontWeight: 'bold' }}>
                        <Chip 
                          label={`#${meeting.meeting_id}`} 
                          size="small" 
                          style={{ 
                            backgroundColor: '#f3f4f6', 
                            fontSize: '10px',
                            height: '22px'
                          }} 
                        />
                      </TableCell>
                      <TableCell style={{ fontSize: '11px' }}>
                        <div className="flex items-center gap-2">
                          <Avatar 
                            className="w-7 h-7"
                            style={{ 
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              fontSize: '10px'
                            }}
                          >
                            {getInitials(meeting.educator_name)}
                          </Avatar>
                          <div>
                            <Typography variant="body2" style={{ fontSize: '11px', fontWeight: 'bold' }}>
                              {meeting.educator_name}
                            </Typography>
                            <Typography variant="caption" style={{ fontSize: '9px', color: '#6b7280' }}>
                              {meeting.educator_email}
                            </Typography>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell style={{ fontSize: '11px' }}>
                        <div className="flex items-center gap-1">
                          <Business style={{ fontSize: '14px', color: '#9ca3af' }} />
                          <Typography variant="body2" style={{ fontSize: '11px' }}>
                            {meeting.institute_name || 'N/A'}
                          </Typography>
                        </div>
                      </TableCell>
                      <TableCell style={{ fontSize: '11px', maxWidth: '200px' }}>
                        <Typography variant="body2" style={{ fontSize: '11px' }} className="line-clamp-2">
                          {meeting.topic || 'No topic'}
                        </Typography>
                      </TableCell>
                      <TableCell style={{ fontSize: '11px' }}>
                        {meeting.meeting_link ? (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<VideoCall style={{ fontSize: '14px' }} />}
                            onClick={() => handleJoinMeeting(meeting.meeting_link)}
                            style={{
                              textTransform: 'none',
                              fontSize: '10px',
                              borderRadius: '8px',
                              padding: '4px 8px'
                            }}
                          >
                            Join
                          </Button>
                        ) : (
                          <Typography variant="caption" style={{ fontSize: '10px', color: '#9ca3af' }}>
                            No link
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell style={{ fontSize: '11px' }}>
                        <div className="flex items-center gap-1">
                          <CalendarToday style={{ fontSize: '14px', color: '#9ca3af' }} />
                          <Typography variant="caption" style={{ fontSize: '10px' }}>
                            {formatDate(meeting.meeting_date)}
                          </Typography>
                        </div>
                      </TableCell>
                      <TableCell style={{ fontSize: '11px' }}>
                        {meeting.assigned_user_name ? (
                          <Chip
                            label={meeting.assigned_user_name}
                            size="small"
                            style={{
                              backgroundColor: '#d1fae5',
                              color: '#065f46',
                              fontSize: '10px',
                              height: '22px'
                            }}
                          />
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            endIcon={<ArrowDropDown style={{ fontSize: '14px' }} />}
                            onClick={(e) => handleSupportDropdownClick(e, meeting)}
                            style={{
                              textTransform: 'none',
                              fontSize: '10px',
                              borderRadius: '8px',
                              padding: '4px 8px'
                            }}
                          >
                            Assign
                          </Button>
                        )}
                      </TableCell>
                      <TableCell style={{ fontSize: '11px' }}>
                        <div className="flex gap-1 justify-center">
                          <Tooltip title="View Details">
                            <IconButton 
                              size="small"
                              onClick={() => handleOpenViewDialog(meeting)}
                              style={{ 
                                backgroundColor: '#dbeafe', 
                                color: '#1e40af',
                                width: '28px',
                                height: '28px'
                              }}
                            >
                              <Visibility style={{ fontSize: '14px' }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Chat">
                            <IconButton 
                              size="small"
                              onClick={() => handleOpenChatDialog(meeting)}
                              style={{ 
                                backgroundColor: '#d1fae5', 
                                color: '#065f46',
                                width: '28px',
                                height: '28px'
                              }}
                            >
                              <ChatIcon style={{ fontSize: '14px' }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Mark as Solved">
                            <IconButton 
                              size="small"
                              onClick={() => handleOpenSolveDialog(meeting)}
                              style={{ 
                                backgroundColor: '#dcfce7', 
                                color: '#16a34a',
                                width: '28px',
                                height: '28px'
                              }}
                            >
                              <CheckCircle style={{ fontSize: '14px' }} />
                            </IconButton>
                          </Tooltip>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Support Dropdown Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleSupportDropdownClose}
        PaperProps={{
          style: {
            maxHeight: 400,
            width: '350px',
            borderRadius: '12px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.15)'
          }
        }}
      >
        {loadingSupportTeam ? (
          <MenuItem disabled>
            <CircularProgress size={20} style={{ marginRight: '12px' }} />
            <Typography variant="body2" style={{ fontSize: '12px' }}>Loading support team...</Typography>
          </MenuItem>
        ) : supportError ? (
          <MenuItem disabled>
            <Typography variant="body2" style={{ fontSize: '12px', color: '#dc2626' }}>
              {supportError}
            </Typography>
          </MenuItem>
        ) : supportTeam && Object.keys(supportTeam).length > 0 ? (
          Object.entries(supportTeam).map(([roleKey, members]) => {
            const roleBadge = getRoleBadgeColor(roleKey);
            return members.map((member, index) => (
              <MenuItem 
                key={`${roleKey}-${index}`}
                onClick={() => handleSupportMemberSelect(roleKey, member)}
                style={{ 
                  padding: '12px 16px',
                  borderBottom: '1px solid #f3f4f6'
                }}
              >
                <ListItemIcon>
                  <Avatar 
                    style={{ 
                      width: '32px', 
                      height: '32px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      fontSize: '12px'
                    }}
                  >
                    {getInitials(member.role_name)}
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <div className="flex items-center gap-2">
                      <Typography variant="body2" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                        {member.role_name}
                      </Typography>
                      <Chip
                        label={roleBadge.label}
                        size="small"
                        style={{
                          backgroundColor: roleBadge.bg,
                          color: roleBadge.color,
                          fontSize: '9px',
                          height: '18px'
                        }}
                      />
                    </div>
                  }
                  secondary={
                    <Typography variant="caption" style={{ fontSize: '10px', color: '#6b7280' }}>
                      {member.email}
                    </Typography>
                  }
                />
              </MenuItem>
            ));
          })
        ) : (
          <MenuItem disabled>
            <Typography variant="body2" style={{ fontSize: '12px' }}>No support team members available</Typography>
          </MenuItem>
        )}
      </Menu>

      {/* View Dialog */}
      <Dialog
        open={openViewDialog}
        onClose={handleCloseViewDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '16px'
          }
        }}
      >
        <DialogTitle 
          className="pb-2"
          style={{ 
            fontSize: '18px', 
            fontWeight: 'bold',
            color: '#1f2937',
            borderBottom: '2px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <div className="flex items-center gap-2">
            <Assignment style={{ fontSize: '22px', color: '#667eea' }} />
            <Typography variant="h6" style={{ fontSize: '16px', fontWeight: 'bold' }}>
              Meeting Details #{selectedMeeting?.meeting_id}
            </Typography>
          </div>
          <IconButton size="small" onClick={handleCloseViewDialog}>
            <Close style={{ fontSize: '20px' }} />
          </IconButton>
        </DialogTitle>

        <DialogContent className="pt-4">
          {selectedMeeting && (
            <Box>
              {/* Educator Information */}
              <Card style={{ marginBottom: '16px', padding: '16px', borderRadius: '12px', backgroundColor: '#f9fafb' }}>
                <Typography variant="subtitle2" style={{ fontSize: '13px', fontWeight: 'bold', color: '#374151', marginBottom: '12px' }}>
                  Educator Information
                </Typography>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2">
                    <Person style={{ fontSize: '16px', color: '#9ca3af' }} />
                    <div>
                      <Typography variant="caption" style={{ fontSize: '10px', color: '#6b7280' }}>Name</Typography>
                      <Typography variant="body2" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                        {selectedMeeting.educator_name}
                      </Typography>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Email style={{ fontSize: '16px', color: '#9ca3af' }} />
                    <div>
                      <Typography variant="caption" style={{ fontSize: '10px', color: '#6b7280' }}>Email</Typography>
                      <Typography variant="body2" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                        {selectedMeeting.educator_email}
                      </Typography>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone style={{ fontSize: '16px', color: '#9ca3af' }} />
                    <div>
                      <Typography variant="caption" style={{ fontSize: '10px', color: '#6b7280' }}>Phone</Typography>
                      <Typography variant="body2" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                        {selectedMeeting.educator_phone || 'N/A'}
                      </Typography>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Business style={{ fontSize: '16px', color: '#9ca3af' }} />
                    <div>
                      <Typography variant="caption" style={{ fontSize: '10px', color: '#6b7280' }}>Institute</Typography>
                      <Typography variant="body2" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                        {selectedMeeting.institute_name || 'N/A'}
                      </Typography>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Meeting Details */}
              <Card style={{ marginBottom: '16px', padding: '16px', borderRadius: '12px', backgroundColor: '#f9fafb' }}>
                <Typography variant="subtitle2" style={{ fontSize: '13px', fontWeight: 'bold', color: '#374151', marginBottom: '12px' }}>
                  Meeting Details
                </Typography>
                <div className="space-y-3">
                  <div className="flex items-start gap-2">
                    <Subject style={{ fontSize: '16px', color: '#9ca3af', marginTop: '2px' }} />
                    <div style={{ flex: 1 }}>
                      <Typography variant="caption" style={{ fontSize: '10px', color: '#6b7280' }}>Topic</Typography>
                      <Typography variant="body2" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                        {selectedMeeting.topic || 'No topic'}
                      </Typography>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Description style={{ fontSize: '16px', color: '#9ca3af', marginTop: '2px' }} />
                    <div style={{ flex: 1 }}>
                      <Typography variant="caption" style={{ fontSize: '10px', color: '#6b7280' }}>Description</Typography>
                      <Typography variant="body2" style={{ fontSize: '12px' }}>
                        {selectedMeeting.description || 'No description'}
                      </Typography>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <CalendarToday style={{ fontSize: '16px', color: '#9ca3af' }} />
                    <div>
                      <Typography variant="caption" style={{ fontSize: '10px', color: '#6b7280' }}>Meeting Date</Typography>
                      <Typography variant="body2" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                        {formatDate(selectedMeeting.meeting_date)}
                      </Typography>
                    </div>
                  </div>
                  {selectedMeeting.meeting_link && (
                    <div className="flex items-center gap-2">
                      <VideoCall style={{ fontSize: '16px', color: '#9ca3af' }} />
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleJoinMeeting(selectedMeeting.meeting_link)}
                        style={{
                          textTransform: 'none',
                          fontSize: '11px',
                          borderRadius: '8px'
                        }}
                      >
                        Join Meeting
                      </Button>
                    </div>
                  )}
                </div>
              </Card>

              {/* Attachments */}
              {selectedMeeting.attachments && selectedMeeting.attachments.length > 0 && (
                <Card style={{ padding: '16px', borderRadius: '12px', backgroundColor: '#f9fafb' }}>
                  <Typography variant="subtitle2" style={{ fontSize: '13px', fontWeight: 'bold', color: '#374151', marginBottom: '12px' }}>
                    Attachments ({selectedMeeting.attachments.length})
                  </Typography>
                  <div className="space-y-2">
                    {selectedMeeting.attachments.map((attachment, index) => (
                      <Card 
                        key={index}
                        style={{ 
                          padding: '12px', 
                          borderRadius: '8px', 
                          backgroundColor: 'white',
                          border: '1px solid #e5e7eb',
                          cursor: 'pointer'
                        }}
                        onClick={() => handleDownloadAttachment(attachment)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Typography style={{ fontSize: '20px' }}>
                              {getFileIcon(attachment.attachment_name)}
                            </Typography>
                            <div>
                              <Typography variant="body2" style={{ fontSize: '11px', fontWeight: 'bold' }}>
                                {getFileName(attachment.attachment_name)}
                              </Typography>
                              <Typography variant="caption" style={{ fontSize: '9px', color: '#6b7280' }}>
                                {getFileSize(attachment.attachment_name)}
                              </Typography>
                            </div>
                          </div>
                          <IconButton size="small">
                            <GetApp style={{ fontSize: '16px', color: '#667eea' }} />
                          </IconButton>
                        </div>
                      </Card>
                    ))}
                  </div>
                </Card>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions className="border-t border-gray-200 p-4">
          <Button
            onClick={handleCloseViewDialog}
            variant="outlined"
            style={{
              textTransform: 'none',
              fontSize: '13px',
              borderRadius: '8px'
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Chat Dialog - IMPROVED WITH STAFF SIDE PATTERN */}
      <Dialog
        open={openChatDialog && !!selectedMeeting}
        onClose={handleCloseChatDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '16px',
            height: '80vh',
            maxHeight: '800px',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle 
          className="pb-2"
          style={{ 
            fontSize: '18px', 
            fontWeight: 'bold',
            color: '#1f2937',
            borderBottom: '2px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
            backgroundColor: '#10B981',
            color: 'white'
          }}
        >
          <div className="flex items-center gap-2">
            <ChatIcon style={{ fontSize: '22px' }} />
            <div>
              <Typography variant="h6" style={{ fontSize: '16px', fontWeight: 'bold', color: 'white' }}>
                Chat - Meeting #{selectedMeeting?.meeting_id}
              </Typography>
              <Typography variant="body2" style={{ fontSize: '11px', color: 'rgba(255,255,255,0.9)' }}>
                {selectedMeeting?.topic}
              </Typography>
            </div>
          </div>
          <IconButton 
            size="small" 
            onClick={handleCloseChatDialog}
            className="text-white hover:bg-white hover:bg-opacity-20"
          >
            <Close style={{ fontSize: '20px' }} />
          </IconButton>
        </DialogTitle>

        <DialogContent 
          className="flex-1 overflow-y-auto"
          style={{ 
            padding: '16px',
            backgroundColor: '#f9fafb',
            flexGrow: 1,
            overflowY: 'auto'
          }}
        >
          {loadingChat ? (
            <Box className="flex justify-center items-center h-full">
              <CircularProgress size={40} />
            </Box>
          ) : chatError ? (
            <Alert severity="error" className="mb-4">
              <Typography variant="body2" style={{ fontSize: '13px' }}>
                {chatError}
              </Typography>
            </Alert>
          ) : chatMessages.length === 0 ? (
            <Box className="flex flex-col items-center justify-center h-full">
              <ChatIcon style={{ fontSize: '48px', color: '#d1d5db' }} />
              <Typography variant="body1" className="text-gray-500 mt-4" style={{ fontSize: '14px' }}>
                No messages yet. Start the conversation!
              </Typography>
            </Box>
          ) : (
            <div className="space-y-3">
              {chatMessages.map((message, index) => {
                const senderInfo = getMessageSenderInfo(message);
                const isFirstInGroup = index === 0 || 
                  getMessageSenderInfo(chatMessages[index - 1]).isSpoc !== senderInfo.isSpoc;
                
                return (
                  <div
                    key={message.id || index}
                    className={`flex ${senderInfo.isSpoc ? 'justify-start' : 'justify-end'}`}
                  >
                    <div className={`flex gap-2 max-w-[75%] ${senderInfo.isSpoc ? 'flex-row' : 'flex-row-reverse'}`}>
                      {isFirstInGroup && (
                        <Avatar
                          className="w-8 h-8"
                          style={{
                            background: senderInfo.avatarBg,
                            fontSize: '12px',
                            fontWeight: 'bold',
                            flexShrink: 0
                          }}
                        >
                          {senderInfo.label.charAt(0)}
                        </Avatar>
                      )}
                      
                      {!isFirstInGroup && (
                        <div className="w-8 flex-shrink-0" />
                      )}
                      
                      <div className="flex-1">
                        {isFirstInGroup && (
                          <div className={`flex items-center gap-2 mb-1 ${senderInfo.isSpoc ? 'justify-start' : 'justify-end'}`}>
                            <Chip
                              label={senderInfo.label}
                              size="small"
                              style={{
                                backgroundColor: senderInfo.bgColor,
                                color: senderInfo.color,
                                fontSize: '10px',
                                height: '20px',
                                fontWeight: 'bold'
                              }}
                            />
                          </div>
                        )}
                        
                        <Card
                          className={`p-3 shadow-md ${
                            senderInfo.isSpoc 
                              ? 'bg-white border-l-4' 
                              : 'bg-gradient-to-r from-green-500 to-emerald-600 text-white'
                          }`}
                          style={{
                            borderRadius: '12px',
                            borderLeftColor: senderInfo.isSpoc ? senderInfo.color : 'transparent'
                          }}
                        >
                          <div className="flex items-start justify-between">
                            <Typography 
                              variant="body2" 
                              style={{ 
                                fontSize: '13px',
                                color: senderInfo.isSpoc ? '#1f2937' : 'white',
                                wordWrap: 'break-word',
                                flex: 1
                              }}
                            >
                              {message.message}
                            </Typography>
                            {message.sending && (
                              <div className="ml-2 flex-shrink-0">
                                <CircularProgress size={12} style={{ color: senderInfo.isSpoc ? '#9ca3af' : 'rgba(255,255,255,0.8)' }} />
                              </div>
                            )}
                          </div>
                          
                          <Typography 
                            variant="caption" 
                            style={{ 
                              fontSize: '10px',
                              color: senderInfo.isSpoc ? '#9ca3af' : 'rgba(255,255,255,0.8)',
                              marginTop: '8px',
                              display: 'block'
                            }}
                          >
                            {formatChatTimestamp(message.created_at)}
                          </Typography>
                        </Card>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={chatMessagesEndRef} />
            </div>
          )}
        </DialogContent>

        <DialogActions 
          className="border-t border-gray-200 p-4"
          style={{ 
            flexShrink: 0,
            backgroundColor: 'white'
          }}
        >
          <Box className="flex flex-col gap-2 w-full">
            <Box className="flex gap-2">
              <TextField
                fullWidth
                multiline
                maxRows={3}
                variant="outlined"
                placeholder="Type your message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendChatMessage();
                  }
                }}
                disabled={sendingMessage || connectionStatus !== 'connected'}
                size="small"
                InputProps={{
                  style: { fontSize: '13px', borderRadius: '12px' }
                }}
              />
              <Button
                variant="contained"
                onClick={sendChatMessage}
                disabled={!newMessage.trim() || sendingMessage || connectionStatus !== 'connected'}
                style={{
                  borderRadius: '12px',
                  minWidth: '100px',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  textTransform: 'none',
                  fontSize: '13px',
                  fontWeight: 'bold',
                  boxShadow: '0 4px 6px rgba(16, 185, 129, 0.3)'
                }}
              >
                {sendingMessage ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <>
                    <Send style={{ fontSize: '16px', marginRight: '4px' }} />
                    Send
                  </>
                )}
              </Button>
            </Box>
            <Typography variant="caption" style={{ fontSize: '10px', color: '#6b7280' }}>
              Press Enter to send, Shift+Enter for new line
            </Typography>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Solve Dialog */}
      <Dialog
        open={openSolveDialog}
        onClose={handleCloseSolveDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '16px'
          }
        }}
      >
        <DialogTitle 
          style={{ 
            fontSize: '18px', 
            fontWeight: 'bold',
            color: '#1f2937',
            borderBottom: '2px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <div className="flex items-center gap-2">
            <CheckCircle style={{ fontSize: '22px', color: '#10b981' }} />
            <Typography variant="h6" style={{ fontSize: '16px', fontWeight: 'bold' }}>
              Mark as Solved
            </Typography>
          </div>
          <IconButton size="small" onClick={handleCloseSolveDialog}>
            <Close style={{ fontSize: '20px' }} />
          </IconButton>
        </DialogTitle>

        <DialogContent className="pt-4">
          {solveSuccess ? (
            <Alert severity="success" className="mb-4">
              <Typography variant="body2" style={{ fontSize: '13px' }}>
                {solveSuccess}
              </Typography>
            </Alert>
          ) : (
            <>
              {solveError && (
                <Alert severity="error" className="mb-4">
                  <Typography variant="body2" style={{ fontSize: '13px' }}>
                    {solveError}
                  </Typography>
                </Alert>
              )}
              
              <Typography variant="body2" style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>
                Please provide a closing remark explaining how this support ticket was resolved.
              </Typography>

              <TextField
                fullWidth
                multiline
                rows={4}
                variant="outlined"
                placeholder="Enter closing remark (e.g., Issue resolved via phone call, problem was...)"
                value={closingRemark}
                onChange={(e) => setClosingRemark(e.target.value)}
                disabled={solvingLoading || solveSuccess}
                style={{ marginBottom: '16px' }}
                InputProps={{
                  style: { fontSize: '13px' }
                }}
              />

              {selectedMeeting && (
                <Card style={{ padding: '12px', borderRadius: '8px', backgroundColor: '#f9fafb' }}>
                  <Typography variant="caption" style={{ fontSize: '11px', color: '#6b7280', marginBottom: '8px', display: 'block' }}>
                    Ticket Details
                  </Typography>
                  <Typography variant="body2" style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '4px' }}>
                    Meeting #{selectedMeeting.meeting_id}
                  </Typography>
                  <Typography variant="body2" style={{ fontSize: '12px', marginBottom: '4px' }}>
                    {selectedMeeting.topic}
                  </Typography>
                  <Typography variant="caption" style={{ fontSize: '10px', color: '#6b7280' }}>
                    Educator: {selectedMeeting.educator_name}
                  </Typography>
                </Card>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions className="border-t border-gray-200 p-4">
          <Button
            onClick={handleCloseSolveDialog}
            variant="outlined"
            disabled={solvingLoading}
            style={{
              textTransform: 'none',
              fontSize: '13px',
              borderRadius: '8px'
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSolveMeeting}
            variant="contained"
            disabled={solvingLoading || solveSuccess || !closingRemark.trim()}
            style={{
              backgroundColor: solveSuccess ? '#10b981' : '#10b981',
              color: 'white',
              textTransform: 'none',
              fontSize: '13px',
              fontWeight: 'bold',
              borderRadius: '8px',
              padding: '8px 24px'
            }}
          >
            {solvingLoading ? 'Saving...' : solveSuccess ? 'Solved!' : 'Mark as Solved'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default InstituteTicket;
